import { useState, useEffect, useMemo, useCallback } from 'react';
import { ConfigProvider, Layout, Typography, Button, Space, Drawer, Menu, Select, Divider, Alert } from 'antd';
import {
  LogoutOutlined,
  MenuOutlined,
  DashboardOutlined,
  UserOutlined,
  EnvironmentOutlined,
  MobileOutlined,
  FileImageOutlined,
  BankOutlined,
  UnorderedListOutlined,
  AimOutlined,
  WarningOutlined,
  DesktopOutlined,
  SettingOutlined,
  CloudOutlined,
  AndroidOutlined,
  AlertOutlined
} from '@ant-design/icons';
import { useSimpleStore } from './stores/simpleStore';
import { useResponsiveImproved } from './hooks/useResponsiveImproved';
import { useAccessibility } from './utils/accessibility';
import { useMenuPermissions } from './hooks/usePermissions';
import { getApiUrl } from './utils/api';
import { fetchWithAuth } from './utils/fetchWithAuth';
import { antdTheme } from './styles/theme';
import { ErrorBoundary } from './components/common';
import SimpleLogin from './components/SimpleLogin';
import RealDashboardImproved from './components/RealDashboardImproved';
import RouteManagement from './components/RouteManagement';
import CtrlDeviceManagement from './components/CtrlDeviceManagement';
import MediaManagement from './components/MediaManagement';
import PlaylistManagement from './components/PlaylistManagement';
import CompanyManagement from './components/CompanyManagement';
import TargetedAdsManagement from './components/TargetedAdsManagement';
import DetourNoticeManagement from './components/DetourNoticeManagement';
import HdmiPlaylistManagement from './components/HdmiPlaylistManagement';
import AppConfigManagement from './components/AppConfigManagement';
import WeatherManagement from './components/WeatherManagement';
import ApkManagement from './components/ApkManagement';
import DisasterManagement from './components/DisasterManagement';
import ConnectionStatus from './components/ConnectionStatus';
import Settings from './components/Settings';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

function LoginApp() {
  const {
    isAuthenticated,
    user,
    loading,
    error,
    selectedCompanyId,
    setSelectedCompany,
    login,
    logout
  } = useSimpleStore();
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [isBackendConnected, setIsBackendConnected] = useState(true);
  const [companies, setCompanies] = useState<Array<{id: number; name: string}>>([]);
  const { isMobile, width, shouldShowSidebar } = useResponsiveImproved();
  const { announce } = useAccessibility();
  const { canAccessMenu, isMenuVisible } = useMenuPermissions();

  // 권한에 따른 메뉴 항목 생성 (메모이제이션) - Hook 규칙 준수를 위해 early return 전으로 이동
  const allMenuItems = useMemo(() => [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: '대시보드'
    },
    {
      key: 'companies',
      icon: <BankOutlined />,
      label: '운수사 관리'
    },
    {
      key: 'routes',
      icon: <EnvironmentOutlined />,
      label: '노선 관리'
    },
    {
      key: 'devices',
      icon: <MobileOutlined />,
      label: '단말기 관리'
    },
    {
      key: 'media',
      icon: <FileImageOutlined />,
      label: '🎬 콘텐츠 관리'
    },
    {
      key: 'playlists',
      icon: <UnorderedListOutlined />,
      label: '📋 플레이리스트 관리'
    },
    {
      key: 'targeted-ads',
      icon: <AimOutlined />,
      label: '🎯 타겟 광고 관리'
    },
    {
      key: 'detour-notices',
      icon: <WarningOutlined />,
      label: '🚧 우회안내 관리'
    },
    {
      key: 'hdmi-playlists',
      icon: <DesktopOutlined />,
      label: '🖥️ HDMI 플레이리스트'
    },
    {
      key: 'app-config',
      icon: <SettingOutlined />,
      label: '⚙️ 앱 설정 관리'
    },
    {
      key: 'weather',
      icon: <CloudOutlined />,
      label: '🌤️ 날씨 + 뉴스 관리'
    },
    {
      key: 'apk-management',
      icon: <AndroidOutlined />,
      label: '📱 APK 버전 관리'
    },
    {
      key: 'disaster',
      icon: <AlertOutlined />,
      label: '🚨 재난정보 관리'
    },
    {
      key: 'system-settings',
      icon: <SettingOutlined />,
      label: '🔧 시스템 설정'
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '프로필 설정'
    }
  ], []);

  // 권한에 따라 필터링된 메뉴 항목 (메모이제이션) - Hook 규칙 준수를 위해 early return 전으로 이동
  // 최적화: isMenuVisible 대신 user.role을 직접 의존성으로 사용하여 불필요한 리렌더링 방지
  const menuItems = useMemo(() =>
    allMenuItems.filter(item => isMenuVisible(item.key)),
    [allMenuItems, user?.role]
  );

  // 권한 검증 및 안전한 메뉴 전환 (메모이제이션) - Hook 규칙 준수를 위해 early return 전으로 이동
  const handleMenuClick = useCallback((menuKey: string) => {
    if (canAccessMenu(menuKey)) {
      setSelectedMenu(menuKey);
      // 스크린 리더에 메뉴 변경 알림
      const menuItem = allMenuItems.find(item => item.key === menuKey);
      if (menuItem) {
        announce(`${menuItem.label} 페이지로 이동했습니다.`);
      }
    } else {
      console.warn(`권한 없는 메뉴 접근 시도: ${menuKey}`);
      announce('해당 메뉴에 접근할 권한이 없습니다.', 'assertive');
    }
  }, [canAccessMenu, allMenuItems, announce]);

  // 운수사 선택 핸들러 메모이제이션 - Hook 규칙 준수를 위해 early return 전으로 이동
  const handleCompanyChange = useCallback((value: number | undefined) => {
    const company = companies.find(c => c.id === value);
    setSelectedCompany(value || null, company?.name || null);
  }, [companies, setSelectedCompany]);

  // fetchCompanies 메모이제이션 - Hook 규칙 준수를 위해 early return 전으로 이동
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await fetchWithAuth(getApiUrl('/api/companies'));

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setCompanies(data.data);
        }
      }
    } catch (error) {
      console.error('운수사 목록 조회 실패:', error);
    }
  }, []);

  // 운수사 목록 가져오기 (admin인 경우만)
  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      fetchCompanies();
    }
  }, [isAuthenticated, user, fetchCompanies]);

  // 운영자의 경우 자동으로 선택된 운수사 설정 (페이지 리로드 시에도 적용)
  useEffect(() => {
    if (isAuthenticated && user?.role === 'operator' && user.company_id && user.company_name) {
      // 현재 선택된 운수사가 없거나 다른 운수사인 경우에만 설정
      if (!selectedCompanyId || selectedCompanyId !== user.company_id) {
        console.log('운영자 자동 운수사 선택:', {
          companyId: user.company_id,
          companyName: user.company_name
        });
        setSelectedCompany(user.company_id, user.company_name);
      }
    }
  }, [isAuthenticated, user, selectedCompanyId, setSelectedCompany]);

  // 로그인되지 않은 경우 로그인 페이지 표시
  if (!isAuthenticated) {
    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#1890ff',
          },
        }}
      >
        <SimpleLogin
          onLogin={login}
          loading={loading}
          error={error}
        />
      </ConfigProvider>
    );
  }

  // 콘텐츠 렌더링 (권한 검증 포함)
  const renderContent = () => {
    // 현재 선택된 메뉴에 대한 접근 권한 확인
    if (!canAccessMenu(selectedMenu)) {
      return (
        <div style={{ padding: isMobile ? 12 : 24 }}>
          <Alert
            message="접근 권한이 없습니다"
            description="이 페이지에 접근할 권한이 없습니다. 관리자에게 문의해주세요."
            type="error"
            showIcon
          />
        </div>
      );
    }

    switch(selectedMenu) {
      case 'dashboard':
        return <RealDashboardImproved />;
        
      case 'companies':
        return <CompanyManagement />;
        
      case 'routes':
        return <RouteManagement />;
        
      case 'devices':
        return <CtrlDeviceManagement />;
        
      case 'media':
        return <MediaManagement />;

      case 'playlists':
        return <PlaylistManagement />;

      case 'targeted-ads':
        return <TargetedAdsManagement />;

      case 'detour-notices':
        return <DetourNoticeManagement />;

      case 'hdmi-playlists':
        return <HdmiPlaylistManagement />;

      case 'apk-management':
        return <ApkManagement />;

      case 'disaster':
        return <DisasterManagement />;

      case 'app-config':
        return <AppConfigManagement />;
        
      case 'weather':
        return <WeatherManagement />;
      
      case 'system-settings':
        return <Settings />;
        
      case 'profile':
        return (
          <div style={{ padding: isMobile ? 12 : 24 }}>
            <Title level={2}>프로필 설정</Title>
            <div style={{ 
              background: '#fff', 
              padding: 24, 
              borderRadius: 8, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
            }}>
              <h3>현재 로그인 정보</h3>
              <p><strong>사용자명:</strong> {user?.username}</p>
              <p><strong>이름:</strong> {user?.name}</p>
              <p><strong>권한:</strong> {user?.role === 'admin' ? '시스템 관리자' : '운수사 운영자'}</p>
              {user?.company_name && (
                <p><strong>소속 운수사:</strong> {user.company_name}</p>
              )}
              {user?.role === 'operator' && (
                <Alert
                  message="운수사 운영자 계정"
                  description={`${user.company_name} 운수사의 데이터만 관리할 수 있습니다.`}
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
              <p style={{ marginTop: 20, color: '#666' }}>
                프로필 수정 기능은 준비 중입니다.
              </p>
            </div>
          </div>
        );
        
      default:
        return (
          <div style={{ padding: isMobile ? 12 : 24 }}>
            <Alert
              message="페이지를 찾을 수 없습니다"
              description="요청하신 페이지를 찾을 수 없습니다."
              type="warning"
              showIcon
            />
          </div>
        );
    }
  };

  // 로그인된 경우 반응형 대시보드 표시
  return (
    <ErrorBoundary>
      <ConfigProvider theme={antdTheme}>
        <Layout style={{ minHeight: '100vh' }}>
        {/* 헤더 */}
        <Header style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          background: '#001529',
          padding: '0 16px',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          {/* 모바일 메뉴 버튼 */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setDrawerOpen(true)}
              style={{ 
                color: 'white', 
                marginRight: 16,
                display: shouldShowSidebar() ? 'none' : 'block'
              }}
              aria-label="메뉴 열기"
            />
            <div style={{ 
              color: 'white', 
              fontSize: isMobile ? 16 : 18,
              fontWeight: 'bold'
            }}>
              🚌 버스 내비게이션
            </div>
          </div>
          
          {/* 사용자 정보 및 로그아웃 */}
          <Space size="small">
            <span style={{
              color: 'white',
              fontSize: isMobile ? 12 : 14,
              display: width <= 480 ? 'none' : 'inline'
            }}>
              {user?.name} ({user?.role === 'admin' ? '관리자' : '운영자'})
            </span>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={logout}
              style={{ color: 'white' }}
              size={isMobile ? 'small' : 'middle'}
            >
              {width <= 480 ? '' : '로그아웃'}
            </Button>
          </Space>
        </Header>

        <Layout>
          {/* 데스크탑 사이드바 */}
          <Sider 
            width={250}
            style={{ 
              background: '#fff',
              display: shouldShowSidebar() ? 'block' : 'none'
            }}
            aria-label="주 네비게이션"
          >
            {/* 운수사 선택 드롭다운 (admin만) */}
            {user?.role === 'admin' && (
              <div style={{ padding: '16px 16px 8px' }}>
                <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                  운수사 선택
                </div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="전체 운수사"
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={companies.map(company => ({
                    value: company.id,
                    label: company.name
                  }))}
                />
                <Divider style={{ margin: '12px 0 0' }} />
              </div>
            )}
            
            {/* 운영자 운수사 정보 표시 */}
            {user?.role === 'operator' && user.company_name && (
              <div style={{ padding: '16px 16px 8px' }}>
                <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                  소속 운수사
                </div>
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f0f2ff',
                  border: '1px solid #d6e4ff',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#1890ff'
                }}>
                  {user.company_name}
                </div>
                <Divider style={{ margin: '12px 0 0' }} />
              </div>
            )}
            
            <Menu
              mode="inline"
              selectedKeys={[selectedMenu]}
              onClick={({ key }) => handleMenuClick(key)}
              items={menuItems}
              style={{ 
                height: (user?.role === 'admin' || (user?.role === 'operator' && user.company_name)) ? 'calc(100% - 80px)' : '100%', 
                borderRight: 0, 
                paddingTop: (user?.role === 'admin' || (user?.role === 'operator' && user.company_name)) ? 8 : 16 
              }}
              role="navigation"
              aria-label="주 메뉴 내비게이션"
            />
          </Sider>

          {/* 모바일 드로어 */}
          <Drawer
            title="메뉴"
            placement="left"
            onClose={() => setDrawerOpen(false)}
            open={drawerOpen}
            styles={{ body: { padding: 0 } }}
          >
            {/* 운수사 선택 드롭다운 (admin만) */}
            {user?.role === 'admin' && (
              <div style={{ padding: '16px 16px 8px' }}>
                <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                  운수사 선택
                </div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="전체 운수사"
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={companies.map(company => ({
                    value: company.id,
                    label: company.name
                  }))}
                />
                <Divider style={{ margin: '12px 0' }} />
              </div>
            )}
            
            {/* 운영자 운수사 정보 표시 (모바일) */}
            {user?.role === 'operator' && user.company_name && (
              <div style={{ padding: '16px 16px 8px' }}>
                <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                  소속 운수사
                </div>
                <div style={{ 
                  padding: '8px 12px',
                  backgroundColor: '#f0f2ff',
                  border: '1px solid #d6e4ff',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#1890ff'
                }}>
                  {user.company_name}
                </div>
                <Divider style={{ margin: '12px 0' }} />
              </div>
            )}
            
            <Menu
              mode="vertical"
              selectedKeys={[selectedMenu]}
              onClick={({ key }) => {
                handleMenuClick(key);
                setDrawerOpen(false);
              }}
              items={menuItems}
              style={{ border: 'none' }}
            />
          </Drawer>
          
          {/* 메인 콘텐츠 */}
          <Layout style={{ 
            padding: isMobile ? '8px' : '16px'
          }}>
            <Content
              style={{
                background: '#f5f5f5',
                minHeight: 'calc(100vh - 64px)',
                padding: isMobile ? '12px' : '24px',
                borderRadius: 8,
                overflow: 'auto'
              }}
            >
              {/* 백엔드 연결 상태 체크 */}
              <ConnectionStatus 
                onConnectionChange={setIsBackendConnected}
                showRetry={true}
              />
              
              {/* 연결 실패 시 콘텐츠 숨김 */}
              {isBackendConnected ? (
                renderContent()
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '48px',
                  color: '#666'
                }}>
                  <h3>백엔드 서버 연결 필요</h3>
                  <p>백엔드 서버에 연결한 후 이용하실 수 있습니다.</p>
                </div>
              )}
            </Content>
          </Layout>
          </Layout>
        </Layout>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

export default LoginApp;